import React, { useState } from "react";
import { Box, Rating, Typography, Tooltip } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

interface StarRatingProps {
  value: number;
  readOnly?: boolean;
  precision?: number;
  size?: "small" | "medium" | "large";
  showLabel?: boolean;
  totalRatings?: number;
  onChange?: (value: number) => void;
}

/**
 * Star rating component for displaying and submitting ratings
 */
const StarRating: React.FC<StarRatingProps> = ({
  value,
  readOnly = false,
  precision = 0.5,
  size = "medium",
  showLabel = true,
  totalRatings,
  onChange,
}) => {
  const [hover, setHover] = useState(-1);

  // Labels for rating tooltips
  const labels: { [index: number]: string } = {
    0.5: "Useless",
    1: "Useless+",
    1.5: "Poor",
    2: "Poor+",
    2.5: "Ok",
    3: "Ok+",
    3.5: "Good",
    4: "Good+",
    4.5: "Excellent",
    5: "Excellent+",
  };

  // Get label based on hover value or current value
  const getLabelText = (value: number) => {
    return labels[value] || "";
  };

  // Format rating value for display
  const formatRating = (value: number) => {
    return value.toFixed(1);
  };

  // Format total ratings for display
  const formatTotalRatings = (count: number) => {
    if (count === 0) return "(No ratings yet)";
    if (count === 1) return "(1 rating)";
    return `(${count} ratings)`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: showLabel ? "column" : "row",
        gap: 0.5,
      }}
    >
      <Rating
        name="star-rating"
        value={value}
        precision={precision}
        readOnly={readOnly}
        onChange={(_, newValue) => {
          if (newValue !== null && onChange) {
            onChange(newValue);
          }
        }}
        onChangeActive={(_, newHover) => {
          setHover(newHover);
        }}
        icon={<StarIcon fontSize="inherit" />}
        emptyIcon={<StarBorderIcon fontSize="inherit" />}
        size={size}
      />

      {showLabel && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {!readOnly ? (
            <Tooltip
              title={hover !== -1 ? getLabelText(hover) : getLabelText(value)}
            >
              <Typography variant="body2" color="text.secondary">
                {hover !== -1 ? formatRating(hover) : formatRating(value)}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {formatRating(value)}
            </Typography>
          )}

          {totalRatings !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {formatTotalRatings(totalRatings)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StarRating;
